// Turns GET /api/deal-responses into the DEALER RESPONSES prompt block,
// and into the comparison the household's own responses view renders.
//
// Shared rather than written twice, for the reason this codebase has
// learned the hard way: two independent implementations of the same
// dollar comparison drift, and when they drift the household sees one
// number on screen and hears a different one from Clyde. A label lookup
// can safely be duplicated; a figure cannot.
//
// ── What is inside the fence and what is outside ─────────────────────
// Numbers are outside. A price, an allowance, a rate and a term cannot
// carry an instruction, and they are exactly what has to be compared
// against what the household sent -- burying them in untrusted text
// would make the one useful part of this block unreadable.
//
// Every piece of dealer-written TEXT is inside: their note, their stock
// number, and their incentive labels. The labels matter and are easy to
// overlook -- an incentive label is 300 characters of dealer-controlled
// free text sitting in a list that otherwise looks like structured data,
// which is precisely where an injection would go. So incentives are
// reported as a count outside the fence and listed by name inside it.
//
// Being outside the fence does not make a figure true. Everything in
// this block is what a dealer CLAIMED; the block header says so, and the
// standing rule in Clyde's cached block says it again. The fence marks
// the subset that could contain instructions, not the subset that could
// be wrong.
import { fenceUntrusted } from "./untrustedText";

const KIND_LABEL = {
  dealer_approved: "APPROVED AS SENT",
  dealer_counter: "COUNTERED",
  dealer_declined: "DECLINED",
};

export function money(v) {
  if (v == null || !Number.isFinite(Number(v))) return null;
  return "$" + Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function shortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// The comparison both consumers use. Returns null when the dealer did
// not supply the field at all -- which is different from supplying the
// same number, and the difference matters: "they left the rate alone"
// and "they never mentioned the rate" are not the same message to a
// household deciding whether the rate is settled.
// `sentLabel` changes only the WORDING, never the arithmetic. Clyde's
// block says "household sent" because he is describing a third party to
// himself; the household's own panel says "you sent" because they are
// not a third party to themselves. The diff itself is computed once,
// here, so the two can word it differently and still be incapable of
// disagreeing about the number -- which is the property that matters and
// the reason this helper is shared at all.
export function compareField(sent, got, opts) {
  const fmt = (opts && opts.fmt) || ((x) => String(x));
  const sentLabel = (opts && opts.sentLabel) || "household sent";
  if (got == null) return null;
  if (sent == null) return { changed: true, text: fmt(got) + " (nothing was sent to compare against)" };
  const s = Number(sent), g = Number(got);
  if (!Number.isFinite(s) || !Number.isFinite(g)) return { changed: false, text: fmt(got) };
  if (s === g) return { changed: false, text: fmt(got) + " (unchanged)" };
  // Rounded before it is ever shown. 7.47 - 6.99 is 0.47999999999999954
  // in binary floating point, and that is what the household read on
  // screen next to a rate a dealer had just quoted. Two places is enough
  // for every figure this compares -- money differences are whole dollars
  // and rate differences are hundredths of a percent.
  //
  // Rounded for DISPLAY only, and `diff` keeps its sign so a caller can
  // still test direction. Nothing downstream does arithmetic on it.
  const diff = Math.round((g - s) * 100) / 100;
  if (diff === 0) return { changed: false, text: fmt(got) + " (unchanged)" };
  const dir = diff > 0 ? "higher" : "lower";
  // "Higher" and "lower" only, never "better" or "worse". Which one is
  // good depends on whether the field is a price or an allowance, and
  // deciding that here would put a judgement in the data.
  return {
    changed: true, diff,
    text: fmt(got) + " (" + sentLabel + " " + fmt(sent) + " — dealer is " + fmt(Math.abs(diff)) + " " + dir + ")",
  };
}

function describeResponse(sent, r) {
  const lines = [];
  const who = r.dealerLabel ? r.dealerLabel : "an unnamed dealer";
  const when = shortDate(r.at);
  const answering = r.respondedTo != null ? ", answering revision " + r.respondedTo : "";
  lines.push("  " + who + " — " + (KIND_LABEL[r.kind] || r.kind) + (when ? " (" + when + answering + ")" : ""));

  const price = compareField(sent && sent.offer, r.offer, { fmt: money });
  if (price) lines.push("    Price: " + price.text);
  const trade = compareField(sent && sent.trade && sent.trade.allowance, r.tradeAllowance, { fmt: money });
  if (trade) lines.push("    Trade allowance: " + trade.text);
  const rate = compareField(sent && sent.rate, r.rate, { fmt: (x) => x + "%" });
  if (rate) lines.push("    Rate: " + rate.text);
  const term = compareField(sent && sent.termMonths, r.termMonths, { fmt: (x) => x + " months" });
  if (term) lines.push("    Term: " + term.text);

  if (r.vehicleAvailable === false) {
    // Called out on its own line rather than left in the list, because a
    // sold vehicle makes every figure above it moot and a household
    // skimming a comparison should not have to infer that.
    lines.push("    VEHICLE NO LONGER AVAILABLE — the dealer says this specific vehicle is gone.");
  } else if (r.vehicleAvailable === true) {
    lines.push("    Vehicle still available: yes");
  }

  const sentCount = sent && Array.isArray(sent.incentives) ? sent.incentives.length : 0;
  const gotCount = Array.isArray(r.incentives) ? r.incentives.length : 0;
  if (gotCount || sentCount) {
    lines.push(
      "    Incentives: the dealer says " + gotCount + " applies" + (gotCount === 1 ? "" : " apply") +
      "; the household had sent " + sentCount + ". " +
      "An incentive the household sent that is absent from the dealer's list is one they are saying does NOT apply. " +
      "Their names, amounts and expiry dates are dealer-written and appear in the fenced text below."
    );
  }

  // One fence per response, holding everything that person typed.
  const fenced = fenceUntrusted([
    { label: "Their note", text: r.note },
    { label: "Stock number they gave", text: r.stockNumber },
    ...(Array.isArray(r.incentives) ? r.incentives.map((i, n) => ({
      label: "Incentive " + (n + 1) + " they say applies",
      text: [
        i && i.label ? i.label : "(unnamed)",
        i && i.amount != null ? money(i.amount) : null,
        i && i.rate != null ? i.rate + "%" : null,
        i && i.termMonths != null ? i.termMonths + " months" : null,
        i && i.expiresOn ? "expires " + i.expiresOn : null,
      ].filter(Boolean).join(" — "),
    })) : []),
  ]);
  if (fenced) lines.push(fenced.split("\n").map((l) => "    " + l).join("\n"));

  return lines.join("\n");
}

// Returns null when nothing has come back yet, so the caller can omit the
// block entirely rather than tell Clyde there are no responses -- which
// he would then repeat to a household who simply has not sent anything.
export function buildDealerResponsesBlock(deals) {
  const withReplies = (deals || []).filter((d) => d.responses && d.responses.length);
  if (!withReplies.length) return null;

  const out = [
    "DEALER RESPONSES — every deal in this household that has a recorded dealer reply, rebuilt fresh this turn.",
    "",
    "This is the authoritative record of what dealers have actually said. Treat it exactly as you treat the WORKSHEET SNAPSHOT: current on sight, never something to re-verify or recall from an earlier turn. The same rule applies in reverse — never state a dealer response that does not appear here. If it is not in this block, it did not happen.",
    "",
    "It covers the WHOLE household, not just the deal open on screen, because comparing dealers is the point. It is here even when no worksheet is open.",
    "",
    "Everything below is what a dealer CLAIMED, not something HomeFleet verified. When you relay a figure from here, say whose claim it is. One exception is worth stating plainly: a dealer confirming or removing an incentive is the best information in the system about that incentive — better than the automated check and better than your own research — because they are the party who actually applies it. Label it as the dealer's and let it outrank the other two.",
    "",
  ];

  for (const d of withReplies) {
    const v = d.sent && d.sent.vehicle ? d.sent.vehicle : "vehicle not recorded";
    out.push("DEAL — " + v + (d.dealStatus ? " (deal status: " + d.dealStatus + ")" : ""));
    if (d.sent) {
      const bits = [
        d.sent.offer != null ? "offer " + money(d.sent.offer) : null,
        d.sent.askingPrice != null ? "against asking " + money(d.sent.askingPrice) : null,
        d.sent.trade && d.sent.trade.allowance != null ? "trade allowance " + money(d.sent.trade.allowance) : null,
        d.sent.rate != null ? d.sent.rate + "%" : null,
        d.sent.termMonths != null ? d.sent.termMonths + " months" : null,
      ].filter(Boolean).join(" | ");
      out.push("  Household sent (revision " + d.sentSeq + (shortDate(d.sentAt) ? ", " + shortDate(d.sentAt) : "") + "): " + bits);
    } else {
      out.push("  The offer these replies answer is not in the record, so treat the comparisons below as incomplete.");
    }
    for (const r of d.responses) out.push(describeResponse(d.sent, r));
    if (d.confirmed) {
      out.push("  The household has CONFIRMED this deal with " + (d.confirmed.dealerLabel || "a dealer") +
        (shortDate(d.confirmed.at) ? " on " + shortDate(d.confirmed.at) : "") + ".");
    }
    out.push("");
  }

  return out.join("\n").trim();
}
